import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  Tag,
  Descriptions,
  Popconfirm,
  Tabs,
  Select,
  Table
} from 'antd';
import {
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  KeyOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { App as AntApp } from 'antd';
import { admin } from '../../services/api';

const { Title, Text } = Typography;

const SswIntegracao = () => {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null);
  const [chave, setChave] = useState('');
  const [danfeResult, setDanfeResult] = useState(null);
  const [consulting, setConsulting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsVisible, setLogsVisible] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  const [logDates, setLogDates] = useState([]);
  const [logFilter, setLogFilter] = useState({ date: '', orgId: '', q: '' });
  const [logResults, setLogResults] = useState([]);
  const [logsSearching, setLogsSearching] = useState(false);

  useEffect(() => {
    load();
    loadLogDates();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await admin.getSswConfig();
      setConfig(data);
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const openConfig = () => {
    form.setFieldsValue({
      dominio: config?.dominio || '',
      cpf: config?.cpf || '',
      usuario: config?.usuario || '',
      senha: '',
      baseUrl: config?.baseUrl || 'https://sistema.ssw.inf.br/bin/ssw0422',
      buscaUrl: config?.buscaUrl || 'https://sistema.ssw.inf.br/bin/ssw0455'
    });
    setModalOpen(true);
  };

  const saveConfig = async (values) => {
    try {
      setLoading(true);
      const payload = { ...values };
      if (!payload.senha && config) delete payload.senha;
      await admin.upsertSswConfig(payload);
      message.success('Configuração SSW salva');
      setModalOpen(false);
      load();
    } catch (e) {
      message.error(e.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const iniciar = async () => {
    try {
      setStarting(true);
      const res = await admin.iniciarSsw();
      setStatus(res);
      message.success(`Navegador iniciado: ${res.estado}`);
    } catch (e) {
      message.error(e.message || 'Erro ao iniciar');
    } finally {
      setStarting(false);
    }
  };

  const checkStatus = async () => {
    try {
      const res = await admin.sswStatus();
      setStatus(res);
    } catch (e) {
      message.error(e.message || 'Erro ao obter status');
    }
  };

  const fechar = async () => {
    try {
      await admin.fecharSsw();
      setStatus(null);
      message.success('Navegador fechado');
    } catch (e) {
      message.error(e.message || 'Erro ao fechar');
    }
  };

  const excluir = async () => {
    try {
      setLoading(true);
      await admin.deleteSswConfig();
      setConfig(null);
      message.success('Configuração SSW excluída');
    } catch (e) {
      message.error(e.message || 'Erro ao excluir');
    } finally {
      setLoading(false);
    }
  };

  const consultarDanfe = async () => {
    if (!chave || chave.length !== 44) {
      message.warning('Chave de acesso deve ter 44 dígitos');
      return;
    }
    try {
      setConsulting(true);
      setDanfeResult(null);
      const res = await admin.sswConsultarDanfe(chave);
      setDanfeResult(res);
    } catch (e) {
      message.error(e.message || 'Erro na consulta');
    } finally {
      setConsulting(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLogsLoading(true);
      const res = await admin.sswLogs();
      setLogs(res?.logs || []);
      setLogsVisible(true);
    } catch (e) {
      message.error(e.message || 'Erro ao carregar logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadLogDates = async () => {
    try {
      const res = await admin.sswLogsDates();
      const dates = res?.dates || [];
      setLogDates(dates);
      setLogFilter((f) => (f.date ? f : { ...f, date: dates[0] || '' }));
    } catch {
      /* datas de log opcionais */
    }
  };

  const searchLogs = async () => {
    try {
      setLogsSearching(true);
      const res = await admin.sswLogsSearch(logFilter);
      setLogResults(res?.logs || []);
    } catch (e) {
      message.error(e.message || 'Erro ao buscar logs');
    } finally {
      setLogsSearching(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>SSW - Automação</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
          <Button type="primary" icon={<EditOutlined />} onClick={openConfig}>
            {config ? 'Editar Configuração' : 'Configurar'}
          </Button>
          {config && (
            <Popconfirm
              title="Excluir configuração SSW?"
              description="Os logs de auditoria serío preservados."
              onConfirm={excluir}
              okText="Sim"
              cancelText="Não"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />}>Excluir</Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      {config && (
        <Card title="Credenciais SSW" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Domínio">{config.dominio}</Descriptions.Item>
            <Descriptions.Item label="CPF">{config.cpf}</Descriptions.Item>
            <Descriptions.Item label="Usuário">{config.usuario}</Descriptions.Item>
            <Descriptions.Item label="Senha"><Tag>********</Tag></Descriptions.Item>
            <Descriptions.Item label="Base URL" span={2}><Text code>{config.baseUrl}</Text></Descriptions.Item>
            <Descriptions.Item label="URL Busca NF" span={2}><Text code>{config.buscaUrl}</Text></Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {!config && (
        <Card style={{ marginBottom: 16 }}>
          <Text type="secondary">Nenhuma configuração SSW encontrada. Clique em "Configurar" para adicionar.</Text>
        </Card>
      )}

      <Tabs
        defaultActiveKey="controle"
        items={[
          {
            key: 'controle',
            label: 'Controle do Navegador',
            children: (
              <>
                <Card title="Controle do Navegador" style={{ marginBottom: 16 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Button type="primary" icon={<PlayCircleOutlined />} onClick={iniciar} loading={starting} disabled={!config}>
                        Iniciar Navegador
                      </Button>
                      <Button icon={<ReloadOutlined />} onClick={checkStatus}>Status</Button>
                      <Button danger onClick={fechar}>Fechar Navegador</Button>
                      <Button icon={<FileTextOutlined />} onClick={loadLogs} loading={logsLoading}>Logs</Button>
                    </Space>
                    {status && (
                      <Descriptions column={3} size="small" style={{ marginTop: 12 }}>
                        <Descriptions.Item label="Estado">
                          <Tag color={status.estado === 'LOGADO' ? 'green' : 'orange'}>{status.estado}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Logado">
                          <Tag color={status.logado ? 'green' : 'red'}>{status.logado ? 'Sim' : 'Não'}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Fila">{status.fila} aguardando</Descriptions.Item>
                        <Descriptions.Item label="URL" span={3}>
                          <Text code style={{ fontSize: 12 }}>{status.url || '-'}</Text>
                        </Descriptions.Item>
                      </Descriptions>
                    )}
                    {logsVisible && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <Text strong>Logs do SSW Service</Text>
                          <Space>
                            <Button size="small" icon={<ReloadOutlined />} onClick={loadLogs} loading={logsLoading}>Atualizar</Button>
                            <Button size="small" onClick={() => setLogsVisible(false)}>Fechar</Button>
                          </Space>
                        </div>
                        <pre style={{ maxHeight: 320, overflow: 'auto', background: '#1e1e1e', color: '#d4d4d4', padding: 12, borderRadius: 4, fontSize: 12, margin: 0 }}>
                          {logs.length > 0
                            ? logs.map((l, i) => `[${l.ts}] [${l.level}] ${l.msg}`).join('\n')
                            : 'Nenhum log registrado.'}
                        </pre>
                      </div>
                    )}
                  </Space>
                </Card>

                <Card title="Consultar DANFE">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Input
                        placeholder="Chave de acesso (44 dígitos)"
                        maxLength={44}
                        style={{ width: 340 }}
                        value={chave}
                        onChange={e => setChave(e.target.value.replace(/\D/g, ''))}
                      />
                      <Button type="primary" icon={<SearchOutlined />} onClick={consultarDanfe} loading={consulting} disabled={!config}>
                        Consultar
                      </Button>
                    </Space>
                    {danfeResult && (
                      <Card size="small" title="Resultado" style={{ marginTop: 8 }}>
                        <pre style={{ maxHeight: 400, overflow: 'auto', background: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12 }}>
                          {JSON.stringify(danfeResult, null, 2)}
                        </pre>
                      </Card>
                    )}
                  </Space>
                </Card>
              </>
            ),
          },
          {
            key: 'logs',
            label: 'Logs',
            children: (
              <Card title="Busca de Logs">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space wrap>
                    <Select
                      placeholder="Data"
                      style={{ width: 160 }}
                      value={logFilter.date || undefined}
                      onChange={(v) => setLogFilter((f) => ({ ...f, date: v || '' }))}
                      options={logDates.map((d) => ({ value: d, label: d }))}
                      allowClear
                    />
                    <Input
                      placeholder="Organization ID"
                      style={{ width: 240 }}
                      value={logFilter.orgId}
                      onChange={(e) => setLogFilter((f) => ({ ...f, orgId: e.target.value }))}
                      allowClear
                    />
                    <Input
                      placeholder="Texto (ex: login, danfe)"
                      style={{ width: 200 }}
                      value={logFilter.q}
                      onChange={(e) => setLogFilter((f) => ({ ...f, q: e.target.value }))}
                      allowClear
                      onPressEnter={searchLogs}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={searchLogs} loading={logsSearching}>
                      Buscar
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={loadLogDates}>Datas</Button>
                  </Space>
                  <Table
                    size="small"
                    rowKey={(r, i) => `${r.ts}-${i}`}
                    dataSource={logResults}
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: 'Nenhum log encontrado' }}
                    columns={[
                      { title: 'Data/Hora', dataIndex: 'ts', width: 180, render: (v) => new Date(v).toLocaleString() },
                      {
                        title: 'Nível',
                        dataIndex: 'level',
                        width: 80,
                        render: (v) => (
                          <Tag color={v === 'ERROR' ? 'red' : v === 'WARN' ? 'orange' : 'blue'}>{v}</Tag>
                        ),
                      },
                      { title: 'Org', dataIndex: 'orgId', width: 220, render: (v) => v || '-' },
                      { title: 'Mensagem', dataIndex: 'msg' },
                    ]}
                  />
                </Space>
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title="Configuração SSW"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
        width={500}
        style={{ top: 0 }}
        maskClosable={false}
        closable={false}
      >
        <Form form={form} layout="vertical" onFinish={saveConfig} style={{ marginTop: 16 }}>
          <Form.Item name="dominio" label="Domínio" rules={[{ required: true }]}>
            <Input placeholder="SEU_DOMINIO" />
          </Form.Item>
          <Form.Item name="cpf" label="CPF" rules={[{ required: true }]}>
            <Input placeholder="00000000000" maxLength={11} />
          </Form.Item>
          <Form.Item name="usuario" label="Usuário" rules={[{ required: true }]}>
            <Input placeholder="SEU_USUARIO" />
          </Form.Item>
          <Form.Item name="senha" label="Senha" rules={config ? [] : [{ required: true }]}>
            <Input.Password
              placeholder={config ? 'Deixe em branco para manter' : 'SUA_SENHA'}
              prefix={<KeyOutlined />}
            />
          </Form.Item>
          <Form.Item name="baseUrl" label="Base URL">
            <Input placeholder="https://sistema.ssw.inf.br/bin/ssw0422" />
          </Form.Item>
          <Form.Item name="buscaUrl" label="URL Busca NF">
            <Input placeholder="https://sistema.ssw.inf.br/bin/ssw0455" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>Salvar</Button>
              <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SswIntegracao;
